import { FC, useCallback } from "react";
import {
  HeadingExtension,
  HeadingExtensionAttributes,
} from "@remirror/extension-heading";
import { useActive, useCommands } from "@remirror/react";
import {
  CommandButtonGroup,
  CommandButton,
  CommandButtonProps,
} from "@remirror/react-ui";

const LEVEL_1 = { level: 1 };
const LEVEL_2 = { level: 2 };

interface ToggleHeadingButtonProps
  extends Omit<
    CommandButtonProps,
    "commandName" | "active" | "enabled" | "attrs" | "onSelect"
  > {
  attrs?: Partial<HeadingExtensionAttributes>;
  onBlur: () => void;
}

const ToggleHeadingButton: FC<ToggleHeadingButtonProps> = ({
  attrs,
  onBlur,
  ...rest
}) => {
  const { toggleHeading } = useCommands<HeadingExtension>();

  const handleSelect = useCallback(() => {
    if (toggleHeading.enabled(attrs)) {
      toggleHeading(attrs);
      onBlur();
    }
  }, [toggleHeading, attrs, onBlur]);

  const active = useActive<HeadingExtension>().heading(attrs);
  const enabled = toggleHeading.enabled(attrs);

  return (
    <CommandButton
      {...rest}
      commandName="toggleHeading"
      active={active}
      enabled={enabled}
      attrs={attrs}
      onSelect={handleSelect}
    />
  );
};

type Props = {
  onBlur: () => void;
};

export const HeadingLevelButtonGroup = ({ onBlur }: Props) => (
  <CommandButtonGroup>
    <ToggleHeadingButton
      aria-label="Heading 1"
      attrs={LEVEL_1}
      onBlur={onBlur}
    />
    <ToggleHeadingButton
      aria-label="Heading 2"
      attrs={LEVEL_2}
      onBlur={onBlur}
    />
  </CommandButtonGroup>
);
